import { NavigableButton } from '@carvana/ds-button';
import CarSwap from '@carvana/ds-icons/TradeinSpot';
import VehicleFront from '@carvana/ds-icons/VehicleFrontSpot';
import Link from '@carvana/ds-link';
import { icon } from '@carvana/ds-styles';
import Switch from '@carvana/ds-switch';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { NavPayloadAction } from '@carvana/merch-native';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useState } from 'react';
import Image from '~/components/common/Image';
import FinanceCard from '~/components/flp/Prequalified/CustomizeYourDeal/FinanceCard';
import { ANALYTICS_EVENTS } from '~/constants/flp/analyticsEvents';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useIsNative } from '~/context/common/DeviceContext';
import { useFinancingContext } from '~/context/flp/FinancingContext';
import { TradeInStatus } from '~/contracts/flp';
import useToggleTradeIn from '~/hooks/flp/services/useToggleTradeIn';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import {
  Badge,
  BadgeIcon,
  BadgeText,
  Thumbnail,
  TradeInVehicle,
  Value,
  VehicleDetails,
  Ymm,
  YmmValue
} from './TradeIn.styles';

const textOptions = {
  default:
    'You could be eligible for tax savings by trading in your current vehicle.',
  enabled: 'Terms include your trade-in vehicle.',
  disabled: "Terms don't include your trade-in vehicle.",
  error:
    'We are having trouble retrieving your trade-in information. This will not be reflected in your pricing.',
  // Trade-In specific states
  expired: 'Terms cannot include a trade-in vehicle with an expired offer.'
};

const TradeIn = () => {
  const isNative = useIsNative();
  const { isLoading, tradeIn } = useFinancingContext();
  const { data: tradeInData, error, enabled } = tradeIn || {};
  const { trackEvent } = useContext(AnalyticsContext);
  const toggleTradeIn = useToggleTradeIn();

  const [toggleStatus, setToggleStatus] = useState<boolean>(
    tradeInData?.applyTradeIn || false
  );
  const status = tradeInData?.status?.toLowerCase() || '';

  const hasError = !!error;
  const isDefault = !hasError && !enabled;

  const hasExpiredTrade = status === TradeInStatus.Expired;
  const badgeText = hasExpiredTrade
    ? `Offer ${status.charAt(0).toUpperCase() + status.slice(1)}`
    : '';

  let text = textOptions.default;
  if (hasError) {
    text = textOptions.error;
  } else if (!!textOptions[status as keyof typeof textOptions]) {
    text = textOptions[status as keyof typeof textOptions];
  } else if (enabled && !tradeInData?.applyTradeIn) {
    text = textOptions.disabled;
  } else if (enabled) {
    text = textOptions.enabled;
  }

  //TODO: handle refresh trade-in & analytics
  const handleToggle = async () => {
    const currentToggleState = toggleStatus;

    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.TOGGLES_TRADEIN, {
      toggleAction: !toggleStatus
    });
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.TOGGLES_TRADEIN, {
      toggleAction: !toggleStatus
    });
    try {
      setToggleStatus(!toggleStatus); // optimistic set the toggle state
      await toggleTradeIn(!toggleStatus);
    } catch (e) {
      setToggleStatus(currentToggleState); // Reset the toggle state after a failed update
    }
  };

  const CardToggle = () => {
    if (enabled && !hasExpiredTrade) {
      return (
        <Switch
          checked={toggleStatus}
          onChange={handleToggle}
          disabled={isLoading}
        />
      );
    }

    return null;
  };

  const handleAddTradeIn = () => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_ADD_TRADEIN);
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_ADD_TRADEIN);
  };

  const handleEditTradeIn = () => {
    datadogRum.addAction(
      DATADOG_EVENTS.PREQUALIFIED.CLICKS_VIEW_TRADEIN_DETAILS
    );
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_VIEW_TRADEIN_DETAILS);
  };

  const vehicleTitle = `${tradeInData?.year} ${tradeInData?.make} ${tradeInData?.model} ${tradeInData?.trim}`;
  const vehicleImage =
    tradeInData?.mobileImage && !tradeInData.mobileImage.includes('generic')
      ? tradeInData.mobileImage
      : null;

  useEffect(() => {
    if (tradeInData?.applyTradeIn !== undefined) {
      setToggleStatus(tradeInData?.applyTradeIn);
    }
  }, [tradeInData]);

  // NOTE(2024/08): Customer is not eligible for TradeIn. Do not display TradeIn section.
  if (tradeIn === null) {
    return null;
  }

  return (
    <FinanceCard
      heading="Trade-in"
      id="trade-in"
      icon={<CarSwap color={icon.primary} size={24} />}
      text={text}
      disabled={hasError}
      Switch={<CardToggle />}
      showDivider={enabled || hasExpiredTrade}
    >
      {isDefault && (
        <NavigableButton
          className="self-start"
          data-analytics-id="Financing Trade-In Add Button"
          data-native-payload={JSON.stringify({
            action: NavPayloadAction.ViewTrades
          })}
          href="/sell-my-car"
          size="sm"
          variant="secondary"
          onClick={handleAddTradeIn}
          data-testid="add-trade-in"
        >
          Add a Trade-In
        </NavigableButton>
      )}

      {(enabled || hasExpiredTrade) && (
        <TradeInVehicle>
          <Thumbnail>
            {vehicleImage ? (
              <Image
                src={vehicleImage}
                alt={vehicleTitle}
                width={88}
                height={88}
              />
            ) : (
              <VehicleFront className="p-20" size={88} />
            )}
          </Thumbnail>
          <VehicleDetails>
            <YmmValue>
              {hasExpiredTrade && (
                <Badge status={status}>
                  <BadgeIcon />
                  <BadgeText>{badgeText}</BadgeText>
                </Badge>
              )}
              <Ymm>
                {tradeInData?.year} {tradeInData?.make} {tradeInData?.model}{' '}
                {tradeInData?.trim}
              </Ymm>
              {!hasExpiredTrade && (
                <Value>
                  Value: $
                  {formatAndDropDecimal(tradeInData?.totalTradeInValue ?? 0)}
                </Value>
              )}
              <Link
                data-analytics-id="Financing Trade-In View Details Button"
                data-native-payload={JSON.stringify({
                  action: NavPayloadAction.ViewTrades
                })}
                href={
                  !isNative &&
                  tradeInData?.appraisalId &&
                  tradeInData.appraisalId > 0
                    ? `/sell-my-car/offer/${tradeInData.appraisalId}`
                    : '/sell-my-car'
                }
                size="sm"
                onClick={handleEditTradeIn}
                data-testid="view-trade-details"
              >
                View Details
              </Link>
            </YmmValue>
          </VehicleDetails>
        </TradeInVehicle>
      )}
    </FinanceCard>
  );
};

export default TradeIn;
